<template>
  <div class="container mt-5">
    <h2 class="text-center mb-4">Host Dashboard</h2>

    <!-- Location Selection Dropdown -->
    <div class="form-group text-center">
      <label for="location">Select Location</label>
      <select v-model="selectedLocation" class="form-control" id="location" required>
        <option value="" disabled>Select a Location</option>
        <option v-for="location in activeLocations" :key="location.id" :value="location.id">
          {{ location.name }}
        </option>
      </select>
    </div>
    <p></p>
    <!-- Navigation Buttons -->
    <div class="d-flex justify-content-center mb-4">
      <button @click="currentView = 'reviewAnswers'" class="btn btn-primary mx-2">
        Review Answers
      </button>
      <button @click="openManageTeamsModal" class="btn btn-primary mx-2">
        Manage Teams
      </button>
      <button @click="openLeaderboardModal" class="btn btn-primary mx-2">
        Leaderboard
      </button>
      <button @click="currentView = 'activateRounds'" class="btn btn-success mx-2">
        Activate Rounds
      </button>
      <button
        type="button"
        class="btn btn-warning mx-2"
        :disabled="!gameId || somBusy"
        @click="startSelectOMatic"
      >
        Start Select-o-Matic
      </button>
      <button
        type="button"
        class="btn btn-outline-warning mx-2"
        :disabled="!gameId"
        @click="showSomModal = true"
      >
        Select-o-Matic board
      </button>
      <button
        type="button"
        class="btn btn-info mx-2"
        :disabled="!selectedLocation"
        @click="openSelectOMaticTv"
      >
        TV display (audience)
      </button>
    </div>
    <h2 class="text-center mb-4">Currently Active</h2>
    <h2 class="text-center mb-4">Round {{ activeRound ? (activeRound.roundNumber + ': ' + activeRound.roundName) : 'Not selected' }}</h2>

    <!-- Active Round Selection Dropdown -->
    <div v-if="selectedLocation" class="form-group text-center">
      <label for="round">Change Active Round</label>
      <select v-model="activeRound" @change="activateRound(activeRound.id)" class="form-control" id="round">
        <option value="" disabled>Select a Round</option>
        <option v-for="round in rounds" :key="round.id" :value="round">
          Round {{ round.roundNumber }}: {{ round.roundName }}
        </option>
      </select>
    </div>

    <!-- Display Components Based on View -->
    <ReviewAnswers
      v-if="selectedLocation && activeRound && !isLoading"
      :selectedLocation="selectedLocation"
      :activeRound="activeRound"
      :rounds="rounds"
      @update-active-round="handleUpdateActiveRound"
    />

    <div v-if="isLoading" class="text-center">
      <p>Loading...</p>
    </div>

    <div v-if="currentView === 'activateRounds' && selectedLocation">
      <ActivateRounds :selectedLocation="selectedLocation" />
    </div>
    <div v-if="!selectedLocation" class="text-center text-danger">
      Please select a location to continue.
    </div>

    <!-- ManageTeams Modal -->
    <div v-if="showManageTeamsModal" class="modal fade show" tabindex="-1" style="display: block;">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Manage Teams</h5>
            <button type="button" class="btn-close" @click="closeManageTeamsModal"></button>
          </div>
          <div class="modal-body">
            <ManageTeams v-if="selectedLocation" :selectedLocation="selectedLocation" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="showManageTeamsModal" class="modal-backdrop fade show"></div>

    <!-- Leaderboard Modal -->
    <div v-if="showLeaderboardModal" class="modal fade show" tabindex="-1" style="display: block;">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Leaderboard</h5>
            <button type="button" class="btn-close" @click="closeLeaderboardModal"></button>
          </div>
          <div class="modal-body">
            <LeaderboardPage v-if="selectedLocation" :selectedLocation="selectedLocation" />
          </div>
        </div>
      </div>
    </div>
    <div v-if="showLeaderboardModal" class="modal-backdrop fade show"></div>

    <SelectOMaticHostModal v-model="showSomModal" :game-id="gameId" />
  </div>
  <footer></footer>
</template>

<script>
import { ref, onMounted, watch } from 'vue';
import { collection, getDocs, query, where, writeBatch, doc } from 'firebase/firestore';
import { db } from '@/firebase';
import moment from 'moment-timezone';
import ReviewAnswers from './ReviewAnswers.vue';
import ActivateRounds from './ActivateRounds.vue';
import ManageTeams from './ManageTeams.vue';
import LeaderboardPage from './LeaderboardPage.vue';
import SelectOMaticHostModal from './SelectOMaticHostModal.vue';
import { startSelectOMaticSession } from '@/services/selectOMaticService';

export default {
  name: 'HostPage',
  components: {
    ReviewAnswers,
    ActivateRounds,
    ManageTeams,
    LeaderboardPage,
    SelectOMaticHostModal,
  },
  setup() {
    const selectedLocation = ref('');
    const activeLocations = ref([]);
    const rounds = ref([]);
    const activeRound = ref(null);
    const currentView = ref('reviewAnswers');
    const gameId = ref('');
    const isLoading = ref(false);
    const showManageTeamsModal = ref(false);
    const showLeaderboardModal = ref(false);
    const showSomModal = ref(false);
    const somBusy = ref(false);

    const fetchActiveLocations = async () => {
      try {
        const locationsSnapshot = await getDocs(collection(db, 'locations'));
        activeLocations.value = locationsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(location => location.isActive);
      } catch (error) {
        console.error('Error fetching locations:', error);
      }
    };

    const fetchGameData = async () => {
      if (!selectedLocation.value) {
        console.error('Selected location is undefined');
        return;
      }

      isLoading.value = true;
      gameId.value = '';
      activeRound.value = null;
      rounds.value = [];

      try {
        const todayET = moment.tz('America/New_York').format('YYYY-MM-DD');
        
        console.log('Fetching game data for:', {
          location: selectedLocation.value,
          date: todayET
        });

        const gamesQuery = query(
          collection(db, 'games'),
          where('location', '==', selectedLocation.value),
          where('date', '==', todayET)
        );
        const gamesSnapshot = await getDocs(gamesQuery);
        
        if (!gamesSnapshot.empty) {
          gameId.value = gamesSnapshot.docs[0].id;
          await fetchRounds();
        } else {
          console.error('No game found for the selected location and date');
        }
      } catch (error) {
        console.error('Error fetching game data:', error);
      } finally {
        isLoading.value = false;
      }
    };

    const fetchRounds = async () => {
      if (!gameId.value) return;

      try {
        const roundsQuery = query(collection(db, `games/${gameId.value}/rounds`));
        const roundsSnapshot = await getDocs(roundsQuery);
        
        rounds.value = roundsSnapshot.docs
          .map(doc => ({ 
            id: doc.id, 
            ...doc.data(),
            gameId: gameId.value
          }))
          .sort((a, b) => a.roundNumber - b.roundNumber); // Add this sorting step
        
        const activeRoundData = rounds.value.find(round => round.isActive);
        if (activeRoundData) {
          activeRound.value = activeRoundData;
        } else {
          console.log('No active round found');
          activeRound.value = null;
        }
      } catch (error) {
        console.error('Error fetching rounds:', error);
        rounds.value = [];
        activeRound.value = null;
      }
    };

    const activateRound = async (roundId) => {
      try {
        const batch = writeBatch(db);

        // Deactivate all rounds
        rounds.value.forEach((round) => {
          const roundDocRef = doc(db, `games/${gameId.value}/rounds`, round.id);
          batch.update(roundDocRef, { isActive: false });
        });

        // Activate the selected round
        const selectedRoundDocRef = doc(db, `games/${gameId.value}/rounds`, roundId);
        batch.update(selectedRoundDocRef, { isActive: true });

        await batch.commit();

        // Update local state
        rounds.value = rounds.value.map(round => ({
          ...round,
          isActive: round.id === roundId
        }));

        activeRound.value = rounds.value.find(round => round.id === roundId);
        if (activeRound.value) {
          activeRound.value.gameId = gameId.value; // Add this line
        }
      } catch (error) {
        console.error('Error activating round:', error);
      }
    };

    const handleUpdateActiveRound = (newActiveRound) => {
      activeRound.value = newActiveRound;
      // Optionally, you might want to refresh the rounds data here
      // fetchRounds();
    };

    watch(selectedLocation, (newLocation) => {
      if (newLocation) {
        console.log('Selected location changed:', newLocation);
        fetchGameData();
      } else {
        gameId.value = '';
        activeRound.value = null;
        rounds.value = [];
      }
    });

    onMounted(async () => {
      await fetchActiveLocations();
    });

    const openManageTeamsModal = () => {
      showManageTeamsModal.value = true;
    };

    const closeManageTeamsModal = () => {
      showManageTeamsModal.value = false;
    };

    const openLeaderboardModal = () => {
      showLeaderboardModal.value = true;
    };

    const closeLeaderboardModal = () => {
      showLeaderboardModal.value = false;
    };

    const startSelectOMatic = async () => {
      if (!gameId.value) return;
      somBusy.value = true;
      try {
        await startSelectOMaticSession(db, gameId.value);
        showSomModal.value = true;
      } catch (error) {
        console.error('Select-o-Matic start error:', error);
        alert(error.message || 'Could not start Select-o-Matic.');
      } finally {
        somBusy.value = false;
      }
    };

    const openSelectOMaticTv = () => {
      if (!selectedLocation.value) return;
      const url = `${window.location.origin}/selectomatic-tv?location=${encodeURIComponent(selectedLocation.value)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    return {
      selectedLocation,
      activeLocations,
      rounds,
      activeRound,
      currentView,
      gameId,
      activateRound,
      isLoading,
      showManageTeamsModal,
      openManageTeamsModal,
      closeManageTeamsModal,
      showLeaderboardModal,
      openLeaderboardModal,
      closeLeaderboardModal,
      handleUpdateActiveRound,
      showSomModal,
      somBusy,
      startSelectOMatic,
      openSelectOMaticTv,
    };
  },
};
</script>

<style scoped>
.text-center {
  text-align: center;
}

footer {
  height: 50px;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.modal-backdrop {
  z-index: 1040;
}

.modal-dialog {
  z-index: 1050;
}
</style>
